import React, { useState, useEffect, useCallback, useRef } from "react";
import { Target, Loader2, Trash2, TrendingUp, TrendingDown, Sparkles, Mail, Check, AlertTriangle, MessageCircle, ShieldCheck } from "lucide-react";
import { tradesRun, tradesList, tradesSetEmail, tradesDelete } from "../lib/api";
import { toast } from "sonner";

const IdeaCard = ({ idea, n }) => {
  const long = (idea.direction || "").toLowerCase() === "long";
  return (
    <div className="bg-[#0a1020] border border-[#00F0FF22] rounded-xl p-3.5 space-y-2" data-testid="trade-idea-card">
      <div className="flex items-center justify-between gap-2">
        <h4 className="text-cyan-50 font-bold text-sm flex items-center gap-2">
          <span className="text-cyan-300/50">{n}.</span>
          <span className="font-mono text-[#00F0FF] text-base">{idea.ticker}</span>
          <span className="text-cyan-200/70 font-normal">{idea.company}</span>
        </h4>
        <span className={`px-2 py-0.5 rounded-full text-xs font-bold border flex items-center gap-1 ${long ? "text-emerald-300 border-emerald-500/40 bg-emerald-500/10" : "text-rose-300 border-rose-500/40 bg-rose-500/10"}`}>
          {long ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}{idea.direction} · {idea.conviction}
        </span>
      </div>
      <p className="text-cyan-100/90 text-sm leading-relaxed">{idea.thesis}</p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
        <div className="bg-[#00F0FF08] rounded-lg px-2 py-1.5"><p className="text-cyan-300/50">Entry</p><p className="text-cyan-50 font-semibold">{idea.entry}</p></div>
        <div className="bg-emerald-500/8 rounded-lg px-2 py-1.5"><p className="text-emerald-300/60">Target</p><p className="text-emerald-200 font-semibold">{idea.target}</p></div>
        <div className="bg-rose-500/8 rounded-lg px-2 py-1.5"><p className="text-rose-300/60">Stop</p><p className="text-rose-200 font-semibold">{idea.stop}</p></div>
        <div className="bg-[#00F0FF08] rounded-lg px-2 py-1.5"><p className="text-cyan-300/50">Timeframe</p><p className="text-cyan-50 font-semibold">{idea.timeframe}</p></div>
      </div>
      <div className="space-y-1 text-xs">
        {idea.catalyst && <p className="text-cyan-200/80"><span className="text-[#00F0FF] font-semibold">Catalyst:</span> {idea.catalyst}</p>}
        {idea.fundamentals && <p className="text-cyan-200/70"><span className="text-cyan-300/60 font-semibold">Fundamentals:</span> {idea.fundamentals}</p>}
        {idea.technicals && <p className="text-cyan-200/70"><span className="text-cyan-300/60 font-semibold">Technicals:</span> {idea.technicals}</p>}
        {idea.sentiment && <p className="text-cyan-200/70"><span className="text-cyan-300/60 font-semibold">Sentiment:</span> {idea.sentiment}</p>}
      </div>
      {idea.jev && (
        <div className="mt-1 rounded-lg border border-[#00F0FF33] bg-[#00F0FF08] px-2.5 py-2 flex flex-wrap items-center gap-2 text-[11px]" data-testid="idea-jev-verdict">
          <span className="text-[#00F0FF] font-bold uppercase tracking-wider flex items-center gap-1">
            <ShieldCheck className="w-3 h-3" /> Jev
          </span>
          {idea.jev.action && (
            <span className={`px-2 py-0.5 rounded-full font-bold border capitalize ${(idea.jev.action === "buy") ? "text-emerald-300 border-emerald-500/40 bg-emerald-500/10" : (idea.jev.action === "sell") ? "text-rose-300 border-rose-500/40 bg-rose-500/10" : "text-amber-300 border-amber-500/40 bg-amber-500/10"}`}>
              {idea.jev.action}{idea.jev.action_confidence != null ? ` · ${Math.round(idea.jev.action_confidence * 100)}%` : ""}
            </span>
          )}
          {idea.jev.conviction_level && <span className="text-cyan-100/80">{idea.jev.conviction_level}</span>}
          {idea.jev.actionable != null && (
            <span className={`${idea.jev.actionable >= 0.5 ? "text-emerald-300/90" : "text-cyan-200/50"}`}>
              {idea.jev.actionable >= 0.5 ? "act now" : "can wait"} · {Math.round(idea.jev.actionable * 100)}%
            </span>
          )}
        </div>
      )}
      {idea.sources?.length > 0 && (
        <p className="text-cyan-300/40 text-[11px] truncate">Sources: {idea.sources.slice(0, 4).join(", ")}</p>
      )}
    </div>
  );
};

const BriefCard = ({ r, onDelete }) => {
  if (r.status === "processing") {
    return (
      <div className="bg-[#060b18] border border-[#00F0FF22] rounded-2xl p-4 flex items-center gap-3" data-testid="trade-processing">
        <Loader2 className="w-5 h-5 animate-spin text-cyan-300 shrink-0" />
        <div>
          <p className="text-cyan-100 text-sm font-semibold">Nexus is surfing the markets…</p>
          <p className="text-cyan-300/40 text-xs">Reading news, fundamentals, technicals & sentiment — 3 ideas coming.</p>
        </div>
      </div>
    );
  }
  if (r.status === "error") {
    return (
      <div className="bg-rose-500/5 border border-rose-500/30 rounded-2xl p-4 flex items-start gap-3" data-testid="trade-error">
        <AlertTriangle className="w-5 h-5 text-rose-300 shrink-0 mt-0.5" />
        <div className="flex-1"><p className="text-rose-200 text-sm font-semibold">Couldn't build the brief</p><p className="text-rose-300/70 text-xs mt-0.5">{r.error || "Try again shortly."}</p></div>
        {onDelete && <button onClick={() => onDelete(r.id)} className="text-cyan-200/40 hover:text-rose-300"><Trash2 className="w-4 h-4" /></button>}
      </div>
    );
  }
  return (
    <div className="bg-[#060b18] border border-[#00F0FF22] rounded-2xl p-4 space-y-3" data-testid="trade-brief-card">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-[#00F0FF] font-bold text-base">🌅 Morning Trade Brief · {r.date}</h3>
          <div className="flex items-center gap-2 mt-1">
            {r.delivered_email && <span className="text-emerald-300/80 text-[11px] flex items-center gap-1"><Mail className="w-3 h-3" /> emailed</span>}
            {r.delivered_wa && <span className="text-emerald-300/80 text-[11px] flex items-center gap-1"><MessageCircle className="w-3 h-3" /> WhatsApp</span>}
            {r.manual && <span className="text-cyan-300/40 text-[11px]">manual run</span>}
          </div>
        </div>
        {onDelete && <button onClick={() => onDelete(r.id)} data-testid="trade-delete-btn" className="text-cyan-200/40 hover:text-rose-300"><Trash2 className="w-4 h-4" /></button>}
      </div>
      {r.market_summary && <p className="text-cyan-100/85 text-sm leading-relaxed">{r.market_summary}</p>}
      <div className="space-y-2.5">
        {(r.ideas || []).map((idea, i) => <IdeaCard key={i} idea={idea} n={i + 1} />)}
      </div>
      {r.disclaimer && <p className="text-cyan-300/40 text-[11px] italic">{r.disclaimer}</p>}
    </div>
  );
};

export default function NexusTrades({ session }) {
  const userId = session.user_id;
  const [items, setItems] = useState([]);
  const [email, setEmail] = useState("");
  const [emailInput, setEmailInput] = useState("");
  const [busy, setBusy] = useState(false);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await tradesList(userId);
      setItems(r.items || []);
      setEmail(r.email || "");
      return r.items || [];
    } catch (e) { return []; }
  }, [userId]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const proc = items.some((x) => x.status === "processing");
    if (proc && !pollRef.current) {
      pollRef.current = setInterval(async () => {
        const list = await load();
        if (!list.some((x) => x.status === "processing")) { clearInterval(pollRef.current); pollRef.current = null; }
      }, 6000);
    }
    return () => { if (pollRef.current && !items.some((x) => x.status === "processing")) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [items, load]);

  const run = async () => {
    setBusy(true);
    try { await tradesRun(userId); toast.success("Nexus is researching — your 3 ideas will appear shortly"); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't start research"); }
    finally { setBusy(false); }
  };

  const saveEmail = async () => {
    if (!emailInput.trim()) return;
    try { const r = await tradesSetEmail(userId, emailInput.trim()); setEmail(r.email); setEmailInput(""); toast.success("Delivery email saved"); }
    catch (e) { toast.error("Couldn't save email"); }
  };

  const del = async (id) => { try { await tradesDelete(userId, id); setItems((h) => h.filter((x) => x.id !== id)); } catch (e) {} };

  return (
    <div className="h-full overflow-y-auto bg-[#030712] p-4 sm:p-6" data-testid="trades-panel">
      <div className="max-w-3xl mx-auto space-y-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h2 className="text-[#00F0FF] font-bold text-2xl flex items-center gap-2"><Target className="w-6 h-6" /> Trade Ideas</h2>
            <p className="text-cyan-200/60 text-sm mt-1">Nexus hunts the market <b>twice a day</b> (pre-open + midday) across web, news, fundamentals, technicals, sentiment & the macro calendar, and brings you <b>5 high-conviction US trade ideas</b> — here, by email and WhatsApp.</p>
          </div>
          <button onClick={run} disabled={busy} data-testid="trades-run-btn"
            className="px-5 py-2.5 rounded-xl text-sm font-bold text-[#030712] bg-[#00F0FF] hover:bg-cyan-300 disabled:opacity-50 flex items-center gap-2 shrink-0">
            {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Starting…</> : <><Sparkles className="w-4 h-4" /> Run now</>}
          </button>
        </div>

        <div className="bg-[#060b18] border border-[#00F0FF22] rounded-xl p-3 flex items-center gap-2 flex-wrap">
          <Mail className="w-4 h-4 text-cyan-300/60" />
          {email ? (
            <span className="text-cyan-100 text-sm flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-emerald-300" /> Emailing to <b>{email}</b></span>
          ) : (
            <span className="text-amber-200/80 text-xs">No delivery email set yet —</span>
          )}
          <div className="flex items-center gap-2 ml-auto">
            <input value={emailInput} onChange={(e) => setEmailInput(e.target.value)} placeholder={email ? "change email" : "your email for the brief"} type="email" data-testid="trades-email-input"
              className="bg-[#0a1020] border border-[#00F0FF22] rounded-lg px-3 py-1.5 text-cyan-50 text-sm w-48" />
            <button onClick={saveEmail} data-testid="trades-email-save" className="px-3 py-1.5 rounded-lg text-xs font-bold text-[#00F0FF] border border-[#00F0FF44] hover:bg-[#00F0FF11]">Save</button>
          </div>
        </div>

        {items.length > 0 ? (
          <div className="space-y-4">{items.map((r) => <BriefCard key={r.id} r={r} onDelete={del} />)}</div>
        ) : (
          <div className="text-center py-16 text-cyan-200/40" data-testid="trades-empty">
            <Target className="w-10 h-10 mx-auto mb-3 opacity-40" />
            <p className="text-sm">No briefs yet. Tap <b>Run now</b> for your first ideas, or wait for the pre-market auto-brief.</p>
          </div>
        )}
      </div>
    </div>
  );
}
